/** Atlassian JSD embed config. The script is loaded lazily after auth bootstrap. */
export const SUPPORT_WIDGET_EMBED = {
  key: '278cc2e6-27aa-42f8-ad9d-f1f8adcfce5c',
  baseUrl: 'https://jsd-widget.atlassian.com',
  scriptSrc: 'https://jsd-widget.atlassian.com/assets/embed.js'
} as const;

const IFRAME_SELECTOR = "iframe#jsd-widget, iframe[src*='jsd-widget.atlassian.com']";
const EMBED_SCRIPT_SELECTOR = `script[src="${SUPPORT_WIDGET_EMBED.scriptSrc}"]`;

/** Launcher-sized hit target; broken embeds sometimes mount near full-viewport. */
const SAFE_MAX_WIDTH_PX = 420;
const SAFE_MAX_HEIGHT_PX = 720;

export function querySupportWidgetIframes(): HTMLIFrameElement[] {
  return Array.from(document.querySelectorAll<HTMLIFrameElement>(IFRAME_SELECTOR));
}

function clearInlineHideStyles(iframe: HTMLIFrameElement): void {
  iframe.style.removeProperty('display');
  iframe.style.removeProperty('visibility');
  iframe.style.removeProperty('opacity');
}

function isLauncherSized(iframe: HTMLIFrameElement): boolean {
  const rect = iframe.getBoundingClientRect();
  // Unmeasured / zero-size iframes are treated as unsafe until Atlassian sizes the launcher.
  if (rect.width <= 0 || rect.height <= 0) return false;
  return rect.width <= SAFE_MAX_WIDTH_PX + 8 && rect.height <= SAFE_MAX_HEIGHT_PX + 8;
}

/** Enable pointer events only for launcher-sized embeds so broken full-page iframes cannot steal clicks. */
export function hardenSupportWidgetPointerEvents(): void {
  const supportOn = document.body.getAttribute('data-support-widget') === 'true';
  const authOn = document.body.getAttribute('data-assistant-auth') === 'true';
  for (const iframe of querySupportWidgetIframes()) {
    if (supportOn && authOn && isLauncherSized(iframe)) {
      iframe.style.setProperty('pointer-events', 'auto', 'important');
    } else {
      iframe.style.setProperty('pointer-events', 'none', 'important');
    }
  }
}

function appendEmbedScript(): void {
  const script = document.createElement('script');
  script.dataset.jsdEmbedded = '';
  script.dataset.key = SUPPORT_WIDGET_EMBED.key;
  script.dataset.baseUrl = SUPPORT_WIDGET_EMBED.baseUrl;
  script.src = SUPPORT_WIDGET_EMBED.scriptSrc;
  script.async = true;
  document.body.appendChild(script);
}

/** Re-run embed.js only when the launcher iframe is missing (not on every visibility flip). */
function ensureSupportWidgetEmbedded(): void {
  if (querySupportWidgetIframes().length > 0) return;
  // Avoid thrashing: if a script tag already exists, wait for it to create the iframe.
  if (document.querySelector(EMBED_SCRIPT_SELECTOR)) return;

  appendEmbedScript();
}

/**
 * Sync body flag + iframe visibility. Re-injects the Atlassian embed when the iframe
 * is missing so users do not need a full refresh.
 */
export function applySupportWidgetVisibility(visible: boolean): void {
  document.body.setAttribute('data-support-widget', visible ? 'true' : 'false');

  if (!visible) {
    for (const iframe of querySupportWidgetIframes()) {
      iframe.style.setProperty('pointer-events', 'none', 'important');
    }
    return;
  }

  for (const iframe of querySupportWidgetIframes()) {
    clearInlineHideStyles(iframe);
  }

  ensureSupportWidgetEmbedded();
  hardenSupportWidgetPointerEvents();
  // Atlassian sizes the iframe asynchronously; re-check shortly after enable.
  window.setTimeout(hardenSupportWidgetPointerEvents, 500);
  window.setTimeout(hardenSupportWidgetPointerEvents, 2000);
}
